import { useEffect } from "react";
import { isIOSApp, iosRequestClipboardAccess } from "../tools/ios";
import {
  isAndroidApp,
  androidGetInstallReferrer,
  androidHasAsyncInstallReferrer,
  androidGetInstallReferrerAsync,
} from "../tools/android";
import type { AndroidInstallReferrerResponse } from "../tools/android";
import { matchUserFingerprint } from "../domains/user-fingerprint/endpoints/matchUserFingerprint";
import { useFirebaseUser } from "../domains/user/hooks/useFirebaseUser";
import { matchesSegmentPrefix } from "../domains/auth/publicBrowserPaths";
import { AUTH_DOOR_PATHS } from "../domains/app/appInstallWall";
import {
  hasLoggedInStore,
  hasUsedDDLStore,
} from "../domains/auth/stores/deferredDeepLinkStore";

function compactLog(value: string): string {
  return value.replace(/\s+/g, " ").trim().slice(0, 1200);
}

function safeStringify(value: unknown): string {
  try {
    const seen = new WeakSet<object>();
    const json = JSON.stringify(value, (_key, current) => {
      if (current instanceof Error) {
        return {
          name: current.name,
          message: current.message,
          stack: current.stack,
        };
      }
      if (typeof current === "object" && current !== null) {
        if (seen.has(current)) return "[Circular]";
        seen.add(current);
      }
      return current;
    });
    return compactLog(json ?? String(value));
  } catch {
    return compactLog(String(value));
  }
}

function errorToLogMessage(error: unknown): string {
  if (error instanceof Error) {
    return compactLog(`${error.name}: ${error.message}`);
  }
  return safeStringify(error);
}

type FlatLogPrimitive = string | number | boolean | null;

function toFlatLogPayload(
  payload: Record<string, unknown>
): Record<string, FlatLogPrimitive> {
  const flatPayload: Record<string, FlatLogPrimitive> = {};
  for (const [key, value] of Object.entries(payload)) {
    if (value === undefined) {
      continue;
    }
    if (
      value === null ||
      typeof value === "string" ||
      typeof value === "number" ||
      typeof value === "boolean"
    ) {
      flatPayload[key] = value;
      continue;
    }
    flatPayload[key] = safeStringify(value);
  }
  return flatPayload;
}

function logAndroidDeepLinkFailure(
  stage: string,
  payload: Record<string, unknown>
): void {
  console.error(
    `DDL_ANDROID_NATIVE_FAILURE ${safeStringify(toFlatLogPayload({ stage, ...payload }))}`
  );
}

/**
 * Validates that the destination URL matches the current host
 */
function isTrustedJuisciHost(hostname: string): boolean {
  const normalized = hostname.toLowerCase();
  return normalized === "juisci.com" || normalized.endsWith(".juisci.com");
}

// Path prefixes that are genuine deferred-deep-link destinations: shareable
// content + the room-invite onboarding entry. A fresh-install clipboard/
// fingerprint value whose path is NOT one of these — arbitrary copied text, an
// app-shell page (/discovery, /search), or a stale link — is ignored so the
// user falls through to the normal /welcome onboarding funnel instead of being
// navigated blindly into the SPA 404 ("No fruits found").
// Keep in sync with the shareable routes built in usePageLinks + AppRoutes.
const DDL_DESTINATION_PATH_PREFIXES = [
  "/post/",
  "/room/",
  "/video/",
  "/infographic/",
  "/playlist/",
  "/author/",
  "/invite/",
  "/gpt/shared/",
  // The practice-invite email link. Mobile web walls it
  // (`installWallFor`), so the app opens it after the install, and the login
  // returns the reader to it.
  "/onboarding/practice",
] as const;

// The app pages that mobile web now walls (`installWallFor`): the wall records
// the page the reader asked for, so the app must accept it back. One entry per
// top-level route segment in `AppRoutes.tsx`, except the account doors and the
// technical pages. Matched on whole segments, so junk clipboard text never
// matches one of them. Keep it in sync when a top-level route is added.
const DDL_APP_PAGE_PATHS = [
  "/discovery",
  "/foryou",
  "/search",
  "/gpt",
  "/rooms",
  "/journal",
  "/work",
  "/company",
  "/videos",
  "/visual-abstract",
  "/visual-abstracts",
  "/profile",
  "/settings",
  "/specialties",
  "/user",
] as const;

function isDeepLinkDestinationPath(pathname: string): boolean {
  const normalized = pathname.toLowerCase();
  // The account doors of the wall, exactly. The wall hands one over when the
  // reader opened it with no page behind it (`getInstallDestinationUrl`), as a
  // website link to `/signup` does: its query carries the website's
  // attribution, which the app records when it opens the door. Exact paths, so
  // `/signup/confirm-email` and its token stay out.
  if (AUTH_DOOR_PATHS.some((path) => normalized === path)) return true;
  if (
    DDL_APP_PAGE_PATHS.some((path) => matchesSegmentPrefix(normalized, path))
  ) {
    return true;
  }
  return DDL_DESTINATION_PATH_PREFIXES.some((prefix) =>
    normalized.startsWith(prefix)
  );
}

function isValidDestinationUrl(url: string): boolean {
  try {
    const destinationUrl = new URL(url, globalThis.location.origin);
    const destinationHost = destinationUrl.hostname.toLowerCase();
    const currentHost = globalThis.location.hostname.toLowerCase();

    // Same-host value: a DIRECT SPA route. Because `new URL(text, origin)`
    // coerces any non-URL clipboard string into a same-origin relative path
    // (e.g. a note "call mom tuesday" -> "/call%20mom%20tuesday"), a same-host
    // destination must additionally match a real deep-link route — otherwise it
    // would navigate straight into the SPA 404. Junk clipboard text lands here
    // and is rejected, so the user falls through to normal onboarding.
    if (destinationHost === currentHost) {
      return isDeepLinkDestinationPath(destinationUrl.pathname);
    }

    // A different but trusted Juisci host (e.g. s.juisci.com short links): the
    // path is an OPAQUE short code resolved/redirected server-side, NOT an SPA
    // route, so the deep-link path allowlist does not apply — trust the host.
    // Non-URL clipboard junk can never reach here: it always coerces to the
    // current host and is handled by the same-host branch above.
    if (
      isTrustedJuisciHost(destinationHost) &&
      isTrustedJuisciHost(currentHost)
    ) {
      return true;
    }

    return false;
  } catch (error) {
    console.error(
      `DDL: invalid destination URL parse failed error="${errorToLogMessage(error)}"`
    );
    return false;
  }
}

const INSTALL_REFERRER_POLL_INTERVAL_MS = 500;
const INSTALL_REFERRER_POLL_MAX_ATTEMPTS = 6;
const FINGERPRINT_MATCH_RETRY_INTERVAL_MS = 600;
const FINGERPRINT_MATCH_RETRY_MAX_ATTEMPTS = 3;
const ANDROID_BRIDGE_WAIT_INTERVAL_MS = 100;
const ANDROID_BRIDGE_WAIT_MAX_MS = 2500;

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function isLikelyAndroidNativeWebView(): boolean {
  return (
    /Android/.test(navigator.userAgent) && /; wv\)/.test(navigator.userAgent)
  );
}

/**
 * Polls androidGetInstallReferrer() up to 6 times with 500ms delays (~3s total).
 * Returns early as soon as a valid result is found.
 */
async function pollInstallReferrer(): Promise<AndroidInstallReferrerResponse | null> {
  for (let i = 0; i < INSTALL_REFERRER_POLL_MAX_ATTEMPTS; i++) {
    const result = androidGetInstallReferrer();
    if (result) {
      console.debug(`DDL: install referrer found on attempt ${i + 1}`);
      return result;
    }
    if (i < INSTALL_REFERRER_POLL_MAX_ATTEMPTS - 1) {
      await delay(INSTALL_REFERRER_POLL_INTERVAL_MS);
    }
  }
  console.error("DDL: install referrer not available after polling");
  return null;
}

/**
 * Polls androidGetInstallReferrerAsync() up to 6 times with 500ms delays (~3s total).
 * Returns early as soon as a valid result is found.
 */
async function pollInstallReferrerAsync(): Promise<AndroidInstallReferrerResponse | null> {
  for (let i = 0; i < INSTALL_REFERRER_POLL_MAX_ATTEMPTS; i++) {
    const result = await androidGetInstallReferrerAsync();
    if (result) {
      console.debug(`DDL: async install referrer found on attempt ${i + 1}`);
      return result;
    }
    if (i < INSTALL_REFERRER_POLL_MAX_ATTEMPTS - 1) {
      await delay(INSTALL_REFERRER_POLL_INTERVAL_MS);
    }
  }
  console.error("DDL Android: failed to poll async install referrer");
  return null;
}

/**
 * Retries fingerprint matching briefly for server-only/null URL results.
 * This covers timing races around first app launch after install.
 */
async function matchUserFingerprintWithRetry(options?: {
  shouldStop?: () => boolean;
}) {
  let lastResult: Awaited<ReturnType<typeof matchUserFingerprint>> = null;
  const shouldStop = options?.shouldStop;

  for (let i = 0; i < FINGERPRINT_MATCH_RETRY_MAX_ATTEMPTS; i++) {
    if (shouldStop?.()) {
      return lastResult;
    }

    const result = await matchUserFingerprint();
    lastResult = result;

    if (shouldStop?.()) {
      return lastResult;
    }

    if (result?.destinationUrl) {
      if (i > 0) {
        console.debug(`DDL: fingerprint match succeeded on attempt ${i + 1}`);
      }
      return result;
    }

    const shouldRetry = result === null || result.matchType === "server";
    if (!shouldRetry || i === FINGERPRINT_MATCH_RETRY_MAX_ATTEMPTS - 1) {
      break;
    }

    await delay(FINGERPRINT_MATCH_RETRY_INTERVAL_MS);
  }

  return lastResult;
}

/**
 * Waits briefly for the Android bridge to be injected on first WebView boot.
 */
async function waitForAndroidBridgeReady(options?: {
  shouldStop?: () => boolean;
}): Promise<boolean> {
  const shouldStop = options?.shouldStop;

  for (
    let elapsed = 0;
    elapsed <= ANDROID_BRIDGE_WAIT_MAX_MS;
    elapsed += ANDROID_BRIDGE_WAIT_INTERVAL_MS
  ) {
    if (shouldStop?.()) {
      return false;
    }
    if (isAndroidApp()) {
      return true;
    }
    if (elapsed < ANDROID_BRIDGE_WAIT_MAX_MS) {
      await delay(ANDROID_BRIDGE_WAIT_INTERVAL_MS);
    }
  }

  return false;
}

/**
 * Extracts and validates a destination URL from install referrer data.
 * Returns null if the referrer doesn't contain a valid URL for our host.
 */
function extractUrlFromReferrer(
  installReferrer: AndroidInstallReferrerResponse | null
): string | null {
  if (!installReferrer?.data) {
    return null;
  }

  const rawData = installReferrer.data.trim();
  const candidates: string[] = [];

  const addCandidate = (value: string | null | undefined) => {
    if (!value) return;
    const normalized = value.trim();
    if (normalized.length === 0) return;
    if (!candidates.includes(normalized)) {
      candidates.push(normalized);
    }
  };

  addCandidate(rawData);

  try {
    addCandidate(decodeURIComponent(rawData));
  } catch (error) {
    logAndroidDeepLinkFailure("install_referrer_extract", {
      operation: "decode_referrer_payload",
      reason: "decode_failed",
      rawData,
      errorName: error instanceof Error ? error.name : null,
      errorMessage:
        error instanceof Error ? error.message : errorToLogMessage(error),
      errorStack: error instanceof Error ? error.stack : null,
    });
  }

  // Fallback: some referrers can be query-string payloads like "url=..."
  if (!rawData.startsWith("http")) {
    const params = new URLSearchParams(rawData);
    const urlParam = params.get("url");
    if (urlParam) {
      addCandidate(urlParam);
      try {
        addCandidate(decodeURIComponent(urlParam));
      } catch {
        // ignore malformed encoded values
      }
    }
  }

  for (const candidate of candidates) {
    if (!candidate.startsWith("http")) {
      continue;
    }
    if (isValidDestinationUrl(candidate)) {
      return candidate;
    }
  }

  // Play Store organic installs return UTM params like
  // "utm_source=google-play&utm_medium=organic".
  console.debug("DDL: install referrer did not contain a valid URL:", rawData);
  return null;
}

/**
 * Hook to handle deferred deep linking via fingerprint matching
 * Only runs on native apps and if the user hasn't logged in before
 */
export function useDeferredDeepLink() {
  const { isAuthenticatedFirebase: isAuthenticated, isLoadingFirebaseUser } =
    useFirebaseUser();

  useEffect(() => {
    if (isAuthenticated) {
      // Don't clean this flag on logout to avoid checking for DDL on the home page
      hasLoggedInStore.set(true);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (isLoadingFirebaseUser || isAuthenticated) {
      return;
    }

    if (hasUsedDDLStore.get() || hasLoggedInStore.get()) {
      return;
    }

    let cancelled = false;
    const isCancelled = () => cancelled;

    // Check for deferred deep link
    if (isIOSApp()) {
      (async () => {
        try {
          const fingerprintMatch = await matchUserFingerprintWithRetry({
            shouldStop: isCancelled,
          });
          if (isCancelled()) {
            return;
          }

          const destinationUrl =
            fingerprintMatch?.destinationUrl ??
            // No fingerprint match, or match found but no URL (ambiguous server match)
            // Check the clipboard for destination URL if available
            (await iosRequestClipboardAccess());
          if (isCancelled()) {
            return;
          }

          if (!destinationUrl || !isValidDestinationUrl(destinationUrl)) {
            return;
          }
          const matchDescription =
            fingerprintMatch === null
              ? "clipboard"
              : `fingerprint (${fingerprintMatch.matchType} match)`;
          console.debug(`DDL found via ${matchDescription}: ${destinationUrl}`);
          if (isCancelled()) {
            return;
          }
          hasUsedDDLStore.set(true);
          globalThis.location.href = destinationUrl;
        } catch (error) {
          console.error(
            `DDL: iOS deferred deep-link flow failed stage=fingerprint_or_clipboard error="${errorToLogMessage(error)}"`
          );
        }
      })();
      return () => {
        cancelled = true;
      };
    }

    if (isAndroidApp() || isLikelyAndroidNativeWebView()) {
      (async () => {
        try {
          console.debug("DDL: checking for deferred deep link on Android");

          const bridgeReady = await waitForAndroidBridgeReady({
            shouldStop: isCancelled,
          });
          if (isCancelled()) {
            return;
          }
          if (!bridgeReady) {
            // Expected in third-party WebViews (WhatsApp, Telegram, etc.) where
            // isLikelyAndroidNativeWebView() matches but no Juisci bridge exists.
            // Use warn (not error) to avoid Sentry noise.
            console.warn(
              `DDL_ANDROID_NATIVE_FAILURE ${safeStringify(toFlatLogPayload({ stage: "bridge_ready_check", operation: "waitForAndroidBridgeReady", reason: "bridge_not_ready_before_timeout", isAndroidApp: isAndroidApp(), isLikelyAndroidNativeWebView: isLikelyAndroidNativeWebView() }))}`
            );
            return;
          }

          // Step 1: Try install referrer
          let installReferrer: AndroidInstallReferrerResponse | null = null;

          if (androidHasAsyncInstallReferrer()) {
            // New native app: use promise-based async method
            console.debug("DDL: polling async install referrer");
            installReferrer = await pollInstallReferrerAsync();
          } else {
            // Old native app: poll synchronous method (client may not be ready yet)
            console.debug("DDL: polling sync install referrer");
            installReferrer = await pollInstallReferrer();
          }
          if (isCancelled()) {
            return;
          }

          const referrerUrl = extractUrlFromReferrer(installReferrer);
          if (referrerUrl) {
            console.debug("DDL found via install referrer: " + referrerUrl);
            if (isCancelled()) {
              return;
            }
            hasUsedDDLStore.set(true);
            globalThis.location.href = referrerUrl;
            return;
          }

          // Step 2: Fall back to fingerprint matching
          console.debug(
            "DDL: install referrer failed, trying fingerprint match"
          );
          const fingerprintMatch = await matchUserFingerprintWithRetry({
            shouldStop: isCancelled,
          });
          if (isCancelled()) {
            return;
          }
          if (!fingerprintMatch) {
            console.debug("DDL: no fingerprint match found");
            return;
          }

          const destinationUrl = fingerprintMatch.destinationUrl;
          if (!destinationUrl || !isValidDestinationUrl(destinationUrl)) {
            if (destinationUrl) {
              logAndroidDeepLinkFailure("fingerprint_redirect", {
                operation: "validate_destination_url",
                reason: "invalid_destination_url",
                destinationUrl,
              });
            } else {
              console.debug("DDL: fingerprint match has no destination URL");
            }
            return;
          }

          console.debug(
            `DDL found via fingerprint (${fingerprintMatch.matchType} match): ${destinationUrl}`
          );
          if (isCancelled()) {
            return;
          }
          hasUsedDDLStore.set(true);
          globalThis.location.href = destinationUrl;
        } catch (error) {
          logAndroidDeepLinkFailure("referrer_or_fingerprint", {
            operation: "run_android_ddl_flow",
            reason: "exception",
            errorName: error instanceof Error ? error.name : null,
            errorMessage:
              error instanceof Error ? error.message : errorToLogMessage(error),
            errorStack: error instanceof Error ? error.stack : null,
          });
        }
      })();
      return () => {
        cancelled = true;
      };
    }

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, isLoadingFirebaseUser]);
}
